import React, { useMemo, useState } from 'react';
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js/min';

/**
 * ⛔⛔ A COUNTRY PICKER FOR THE PARTNER'S MOBILE — Card SALES-REP-EASE, 4 Oct 2026.
 *
 * Daniel, after issuing a real login end to end: "the phone number should have
 * a drop down for what country it is so it is easy to fill out the form. This
 * is to make it easier for the sales rep."
 *
 * Before this, every staff surface that took a mobile demanded full E.164 and
 * refused anything else, with the standing line "we do not guess a country".
 * That was the right refusal while there was nothing to ask — but the fix for
 * "do not guess" is to ASK, not to make the rep do the conversion in their head
 * at an event table while the affiliate reads digits off a handset.
 *
 * ── ⛔⛔ THE SERVER CONTRACT DOES NOT MOVE, AND THAT IS THE POINT ───────────
 *
 * The payload is still ONE E.164 STRING. database/partnerPhone.js
 * normalizeMobile() is still the strict authority and is NOT loosened by this
 * card — it still demands `+` and a country digit, and it still refuses
 * anything else. What changed is WHERE the conversion happens: the browser now
 * does it, visibly, from a country the staff member CHOSE, and shows the
 * result before submit. A number that reaches the server still looks exactly
 * like a number that reached it yesterday.
 *
 * ⚠ So this component is CONVENIENCE, not a gate. If it were bypassed entirely
 * the server would refuse a national number exactly as it always has.
 *
 * ── ⚠⚠ A MAINTAINED LIBRARY, NOT A REGEX ──────────────────────────────────
 *
 * libphonenumber-js, `min` metadata, per the card. A hand-rolled formatter is
 * the classic way to produce a number that looks right and dials nowhere:
 * national trunk prefixes (the UK's leading 0, dropped in E.164; Italy's,
 * which is KEPT) are not a pattern you can guess, they are a per-country fact
 * the library carries.
 *
 * ⚠ THE PRECISION LIMIT OF `min`, STATED PLAINLY. The min metadata validates by
 * POSSIBLE LENGTH for the country, not against each carrier's number ranges.
 * So "(305) 555-014" is rejected (nine digits is not a possible US length) and
 * a correctly-shaped but unassigned number is accepted. That is the right trade
 * for an inline hint whose job is to catch a mistyped digit count before the
 * rep hangs up; Cognito remains the authority on whether a handset exists, and
 * no SMS is sent at all yet.
 */

/**
 * ⚠ DANIEL'S ORDER, NOT ALPHABETICAL. These are the markets the sales team
 * actually works, so they sit above the long list rather than in it. The US is
 * first AND is the default, because it is the overwhelming majority and a
 * default that is usually right costs the rep nothing.
 */
export const COMMON_COUNTRIES = Object.freeze(['US', 'CA', 'MX', 'PR', 'DO', 'CO', 'VE', 'ES', 'GB']);

export const DEFAULT_COUNTRY = 'US';

/**
 * ⚠ Names come from the platform (Intl), not from a table in this repo: a
 * hand-kept list of 245 country names goes stale and nobody notices, and
 * Electron and Node both ship full ICU. The ISO code is the fallback so a
 * missing name degrades to something still selectable, never to a blank row.
 */
const REGION_NAMES = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch (e) {
    return null;
  }
})();

export function countryName(code) {
  try {
    const n = REGION_NAMES && REGION_NAMES.of(code);
    return n && n !== code ? n : code;
  } catch (e) {
    return code;
  }
}

/** "United States (+1)" — the name the rep reads, the code they confirm. */
export function countryLabel(code) {
  return `${countryName(code)} (+${getCountryCallingCode(code)})`;
}

/**
 * ⛔ THE CONVERSION, IN ONE PLACE AND TAKING THE COUNTRY.
 *
 * ⚠⚠ THE COUNTRY ARGUMENT IS THE WHOLE FEATURE. Drop it and
 * "(305) 555-0142" parses to nothing at all — there is no country to attach
 * the digits to — so the field would silently refuse every national number a
 * rep types. tests/react/partnerCountryPicker.test.js plants exactly that.
 *
 * @returns {{ e164: string, display: string } | null} null when the number is
 *          not valid for the chosen country.
 */
export function toE164(nationalText, country) {
  const raw = String(nationalText || '').trim();
  if (!raw) return null;
  const parsed = parsePhoneNumberFromString(raw, country);
  if (!parsed || !parsed.isValid()) return null;
  return { e164: parsed.number, display: parsed.formatInternational() };
}

export default function MobileField({
  onChange,
  disabled,
  idPrefix = 'mobile',
  numberLabel = 'Partner mobile number',
  autoFocus,
}) {
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [text, setText] = useState('');

  /**
   * ⚠ Built once. getCountries() is 245 entries and Intl.DisplayNames is not
   * free; rebuilding this on every keystroke made the field perceptibly laggy
   * on the first draft.
   */
  const others = useMemo(() => getCountries()
    .filter((c) => !COMMON_COUNTRIES.includes(c))
    .map((c) => ({ code: c, label: countryLabel(c) }))
    .sort((a, b) => a.label.localeCompare(b.label)), []);

  const result = toE164(text, country);
  const showError = text.trim().length > 0 && !result;

  function emit(nextText, nextCountry) {
    const r = toE164(nextText, nextCountry);
    /**
     * ⛔ THE PARENT ONLY EVER HOLDS E.164 OR NOTHING. Handing it a half-typed
     * national string would put the parent one careless `||` away from posting
     * "(305) 555-01" to the server, and the empty string is what keeps every
     * existing submit button disabled until the number is real.
     */
    if (onChange) onChange(r ? r.e164 : '');
  }

  function onCountry(e) {
    const next = e.target.value;
    setCountry(next);
    /**
     * ⚠ THE TYPED DIGITS ARE KEPT AND RE-INTERPRETED, not cleared. The rep
     * types the number first and notices the country second at least as often
     * as the other way round; wiping the field on a country change would punish
     * the commoner order.
     */
    emit(text, next);
  }

  function onText(e) {
    const next = e.target.value;
    /**
     * ⚠⚠ DO NOT REFORMAT WHILE DELETING. AsYouType re-inserts the separator the
     * user just deleted, so backspacing over ") " becomes an infinite fight
     * with the caret — measured on the first draft, and the single most common
     * complaint about as-you-type fields everywhere.
     */
    const formatted = next.length < text.length ? next : new AsYouType(country).input(next);
    setText(formatted);
    emit(formatted, country);
  }

  const selectId = `${idPrefix}-country`;
  const inputId = `${idPrefix}-national`;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', flex: '1 1 320px', minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 8, width: '100%', flexWrap: 'wrap' }}>
        <select
          id={selectId}
          /* ⚠ NOT "...mobile number" — the national input's own label ends in
             those two words, and a getByLabelText(/mobile number/i) in three
             existing suites would then match BOTH controls and throw. Measured,
             not guessed: that is exactly how this first broke. */
          aria-label="Country for this mobile"
          value={country}
          disabled={disabled}
          onChange={onCountry}
          style={{ flex: '1 1 190px', minWidth: 0, maxWidth: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #CED4DA', borderRadius: 5, background: '#fff' }}
        >
          <optgroup label="Common">
            {COMMON_COUNTRIES.map((c) => (
              <option key={c} value={c}>{countryLabel(c)}</option>
            ))}
          </optgroup>
          <optgroup label="All countries">
            {others.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </optgroup>
        </select>

        <input
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          autoFocus={autoFocus}
          value={text}
          disabled={disabled}
          onChange={onText}
          placeholder="(305) 555-0142"
          aria-label={`${numberLabel}, typed however the partner reads it out`}
          aria-invalid={showError ? 'true' : undefined}
          style={{ flex: '1 1 170px', minWidth: 0, padding: '6px 8px', fontSize: 12, border: `1px solid ${showError ? '#F5C2C7' : '#CED4DA'}`, borderRadius: 5 }}
        />
      </div>

      {/* ⛔ WHAT WILL ACTUALLY BE SAVED, BEFORE SUBMIT. The rep reads this back
          to the partner; it is the last chance to catch a wrong country, and a
          wrong country is the failure this whole control exists to prevent. */}
      {result && (
        <div data-testid={`${idPrefix}-preview`} style={{ width: '100%', fontSize: 11, color: '#1E6B52' }}>
          Will be saved as <strong style={{ fontFamily: 'monospace' }}>{result.display}</strong>
        </div>
      )}

      {/* ⚠ INLINE AND BEFORE ANY CALL. The rep is standing with the partner;
          learning now means asking again, learning after a round trip means
          calling back. */}
      {showError && (
        <div data-testid={`${idPrefix}-error`} role="alert" style={{ width: '100%', fontSize: 11, color: '#991B1B' }}>
          That is not a valid {countryName(country)} number. Check the digits and the country with the partner.
        </div>
      )}
    </div>
  );
}
